/**
 * Process accordion — desktop: ScrollTrigger pin + scrub steps/progress.
 * Mobile: tap to expand.
 */
(function () {
  const ROOT = '[data-process-accordion]';
  const TRACK = '[data-rpa-scroll-track]';
  const STICKY = '[data-rpa-scroll-sticky]';
  const DESKTOP_MQ = '(min-width: 990px) and (min-height: 601px)';
  const SHORT_DESKTOP_MQ = '(min-width: 990px) and (max-height: 600px)';

  function gsapReady() {
    return typeof gsap !== 'undefined' && typeof ScrollTrigger !== 'undefined';
  }

  function accentFromRoot(root) {
    const fromData = root.dataset && root.dataset.rpaAccent;
    if (fromData && fromData.trim()) return fromData.trim();
    try {
      const v = getComputedStyle(root).getPropertyValue('--rpa-accent').trim();
      if (v) return v;
    } catch (e) {
      /* ignore */
    }
    return '#BDBA5A';
  }

  function stepCount(root, cards) {
    const n = parseInt(root.dataset.rpaSteps, 10);
    return !Number.isNaN(n) && n > 0 ? n : cards.length;
  }

  function progressToStep(progress, n) {
    if (n <= 1) {
      return { index: 0, pct: Math.min(100, Math.max(0, progress * 100)) };
    }
    const scaled = progress * n;
    const index = Math.min(n - 1, Math.floor(scaled));
    const pct = Math.min(100, Math.max(0, (scaled - index) * 100));
    return { index, pct };
  }

  function syncProgress(root, pct, activeIndex) {
    const p = Math.max(0, Math.min(100, pct));
    const v = `${p.toFixed(2)}%`;
    const accent = accentFromRoot(root);
    root.style.setProperty('--rpa-pct', v);
    root.querySelectorAll('.relab-process-accordion__card').forEach((card, i) => {
      const fill = card.querySelector('.relab-process-accordion__progress-fill');
      if (!fill) return;
      fill.style.width = i === activeIndex ? v : '0%';
      fill.style.backgroundColor = accent;
    });
  }

  function destroyScroll(root) {
    if (root._rpaMatchMedia) {
      root._rpaMatchMedia.revert();
      root._rpaMatchMedia = null;
    }
    if (root._rpaScrollTrigger) {
      root._rpaScrollTrigger.kill();
      root._rpaScrollTrigger = null;
    }
    root._rpaStep = 0;
    root._rpaPct = 0;
    root.removeAttribute('data-rpa-scroll-mode');
    root.classList.remove('is-rpa-pinned');
  }

  function init(root) {
    destroyScroll(root);

    if (root._rpaClick) {
      root.removeEventListener('click', root._rpaClick);
      root._rpaClick = null;
    }

    const cards = root.querySelectorAll('.relab-process-accordion__card');
    const track = root.querySelector(TRACK);
    const sticky = root.querySelector(STICKY);
    if (!cards.length) return;

    const n = stepCount(root, cards);
    let lastIndex = -1;

    function setStep(index, stepPct, reveal) {
      const i = Math.max(0, Math.min(index, n - 1));
      const pct = typeof stepPct === 'number' ? stepPct : 100;
      const indexChanged = i !== lastIndex;

      cards.forEach((card, idx) => {
        const active = idx === i;
        card.classList.toggle('is-active', active);
        card.classList.toggle('is-reveal', !!(reveal && active));

        const btn = card.querySelector('.relab-process-accordion__card-toggle');
        const panel = card.querySelector('.relab-process-accordion__card-panel');
        if (btn) btn.setAttribute('aria-expanded', active ? 'true' : 'false');
        if (panel) panel.setAttribute('aria-hidden', active ? 'false' : 'true');
      });

      syncProgress(root, pct, i);
      root._rpaStep = i;
      root._rpaPct = pct;
      lastIndex = i;

      if (indexChanged && reveal) {
        root.dataset.rpaActiveStep = String(i);
      }
    }

    root._rpaClick = (ev) => {
      const btn = ev.target.closest && ev.target.closest('.relab-process-accordion__card-toggle');
      if (!btn || !root.contains(btn)) return;
      const card = btn.closest('.relab-process-accordion__card');
      const idx = Array.prototype.indexOf.call(cards, card);
      if (idx < 0) return;

      if (root._rpaScrollTrigger) {
        const st = root._rpaScrollTrigger;
        const span = st.end - st.start;
        const target = st.start + (idx / n) * span;
        st.scroll(target);
        return;
      }

      setStep(idx, 100, true);
    };
    root.addEventListener('click', root._rpaClick);

    function initMobile() {
      let start = 0;
      cards.forEach((c, i) => {
        if (c.classList.contains('is-active')) start = i;
      });
      setStep(start, 100, true);
    }

    if (!gsapReady() || !track || !sticky) {
      initMobile();
      return;
    }

    gsap.registerPlugin(ScrollTrigger);

    root._rpaMatchMedia = gsap.matchMedia();

    root._rpaMatchMedia.add(DESKTOP_MQ, () => {
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        initMobile();
        return;
      }

      root.setAttribute('data-rpa-scroll-mode', '1');
      lastIndex = -1;

      const scrollDistance = () => window.innerHeight * Math.max(1, n);

      root._rpaScrollTrigger = ScrollTrigger.create({
        trigger: track,
        start: 'top top',
        end: () => `+=${scrollDistance()}`,
        pin: sticky,
        pinSpacing: true,
        scrub: 0.45,
        invalidateOnRefresh: true,
        anticipatePin: 1,
        onUpdate: (self) => {
          const { index, pct } = progressToStep(self.progress, n);
          setStep(index, pct, true);
        },
        onEnter: () => {
          root.classList.add('is-rpa-pinned');
          setStep(0, 0, true);
        },
        onLeave: () => {
          root.classList.remove('is-rpa-pinned');
          setStep(n - 1, 100, true);
        },
        onEnterBack: () => {
          root.classList.add('is-rpa-pinned');
          setStep(n - 1, 100, true);
        },
        onLeaveBack: () => {
          root.classList.remove('is-rpa-pinned');
          setStep(0, 0, true);
        },
      });

      ScrollTrigger.refresh();

      return () => {
        if (root._rpaScrollTrigger) {
          root._rpaScrollTrigger.kill();
          root._rpaScrollTrigger = null;
        }
        root.removeAttribute('data-rpa-scroll-mode');
        root.classList.remove('is-rpa-pinned');
      };
    });

    root._rpaMatchMedia.add(SHORT_DESKTOP_MQ, () => {
      // Short viewports cannot fit the pinned 100vh stage; scrubbing would
      // steal scroll from the clipped copy. Use a normal accordion instead.
      root.setAttribute('data-rpa-scroll-mode', '2');
      initMobile();
      return () => {
        root.removeAttribute('data-rpa-scroll-mode');
      };
    });

    root._rpaMatchMedia.add('(max-width: 989px)', () => {
      initMobile();
    });
  }

  function boot() {
    if (gsapReady()) {
      gsap.registerPlugin(ScrollTrigger);
    }
    document.querySelectorAll(ROOT).forEach(init);
    if (gsapReady()) {
      ScrollTrigger.refresh();
    }
  }

  document.addEventListener('shopify:section:load', (e) => {
    const root = document.querySelector(`#shopify-section-${e.detail.sectionId} ${ROOT}`);
    if (root) {
      init(root);
      if (gsapReady()) ScrollTrigger.refresh();
    }
  });

  document.addEventListener('shopify:section:unload', (e) => {
    const root = document.querySelector(`#shopify-section-${e.detail.sectionId} ${ROOT}`);
    if (root) destroyScroll(root);
  });

  window.addEventListener('load', () => {
    if (gsapReady()) ScrollTrigger.refresh();
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();